import { useState, useEffect } from "react";
import Select from "react-select";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import {
  getMentoresSeguimientoService,
  getAsignacionesMentorService,
  inactivarAsignacionService,
  activarAsignacionService,
  eliminarAsignacionService,
} from "../../services/seguimiento/seguimiento.service";
import Loader from "../../components/UI/Loader";
import AsignacionModal from "../../components/seguimiento/AsignacionModal";
import ReasignarMentorModal from "../../components/seguimiento/ReasignarMentorModal";

const SeguimientoPage = () => {
  const [mentores, setMentores] = useState([]);
  const [selectedMentor, setSelectedMentor] = useState(null);
  const [asignaciones, setAsignaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAsignaciones, setLoadingAsignaciones] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reasignarTarget, setReasignarTarget] = useState(null);

  useEffect(() => {
    const fetchMentores = async () => {
      setLoading(true);
      try {
        const data = await getMentoresSeguimientoService();
        const options = data.map((m) => ({ value: m.id, label: m.label }));
        setMentores(options);
      } catch (error) {
        toast.error("Error al cargar los mentores");
      } finally {
        setLoading(false);
      }
    };
    fetchMentores();
  }, []);

  const fetchAsignaciones = async (mentorId) => {
    setLoadingAsignaciones(true);
    try {
      const data = await getAsignacionesMentorService(mentorId);
      setAsignaciones(data);
    } catch (error) {
      toast.error("Error al cargar las asignaciones");
    } finally {
      setLoadingAsignaciones(false);
    }
  };

  useEffect(() => {
    if (selectedMentor) {
      fetchAsignaciones(selectedMentor.value);
    } else {
      setAsignaciones([]);
    }
  }, [selectedMentor]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      if (newStatus === "ACTIVA") {
        await activarAsignacionService(id);
        toast.success("Asignación activada");
      } else {
        await inactivarAsignacionService(id);
        toast.success("Asignación inactivada");
      }
      fetchAsignaciones(selectedMentor.value);
    } catch (error) {
      toast.error("Error al cambiar el estatus de la asignación");
    }
  };

  const handleDelete = async (id) => {
    const isConfirmed = window.confirm(
      "¿Estás seguro? La asignación será eliminada y el estudiante volverá a estar sin asignar."
    );

    if (isConfirmed) {
      try {
        await eliminarAsignacionService(id);
        toast.success("Asignación eliminada");
        fetchAsignaciones(selectedMentor.value);
      } catch (error) {
        toast.error("Error al eliminar la asignación");
      }
    }
  };

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-body p-24">
        <div className="d-flex align-items-center justify-content-between mb-24">
          <h4 className="mb-0">Asignación Mentor-Estudiante</h4>
        </div>

        <div className="row mb-4">
          <div className="col-md-6 col-12">
            <label className="form-label">Seleccionar Mentor</label>
            <Select
              options={mentores}
              value={selectedMentor}
              onChange={setSelectedMentor}
              placeholder="Selecciona un mentor..."
              isLoading={loading}
              isClearable
              className="react-select"
            />
          </div>
          <div className="col-md-6 col-12 d-flex align-items-end justify-content-md-end mt-3 mt-md-0">
            {selectedMentor && (
              <button
                className="btn btn-primary d-flex align-items-center gap-2"
                onClick={() => setIsModalOpen(true)}
              >
                <Icon icon="mdi:plus" width="20" />
                Asignar Estudiantes
              </button>
            )}
          </div>
        </div>

        {selectedMentor ? (
          loadingAsignaciones ? (
            <Loader center text="Cargando asignaciones..." delay={100} />
          ) : asignaciones.length > 0 ? (
            <div className="table-responsive">
              <table className="table bordered-table mb-0">
                <thead>
                  <tr>
                    <th scope="col">Estudiante</th>
                    <th scope="col">Plantel / Escuela</th>
                    <th scope="col">Origen</th>
                    <th scope="col">Motivo</th>
                    <th scope="col">Estatus</th>
                    <th scope="col" className="text-end">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {asignaciones.map((asig) => (
                    <tr key={asig.id}>
                      <td>{asig.estudiante_nombre}</td>
                      <td>
                        <span className="d-block text-sm fw-semibold">
                          {asig.plantel_nombre}
                        </span>
                        <span className="text-xs text-secondary-light">
                          {asig.escuela_nombre}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`badge text-sm fw-semibold ${
                            asig.origen === "AUTOMATICO"
                              ? "bg-info-focus text-info-main"
                              : "bg-warning-focus text-warning-main"
                          } px-24 py-9 rounded-pill`}
                        >
                          {asig.origen}
                        </span>
                      </td>
                      <td>{asig.motivo_reasignacion || "-"}</td>
                      <td>
                        <span
                          className={`badge text-sm fw-semibold ${
                            asig.estatus === "ACTIVA"
                              ? "bg-success-focus text-success-main"
                              : "bg-danger-focus text-danger-main"
                          } px-24 py-9 rounded-pill`}
                        >
                          {asig.estatus}
                        </span>
                      </td>
                      <td className="text-end">
                        <div className="d-flex align-items-center justify-content-end gap-2">
                          {asig.estatus === "ACTIVA" ? (
                            <>
                              <button
                                type="button"
                                className="btn btn-sm btn-info-600 rounded-pill px-3"
                                onClick={() =>
                                  setReasignarTarget({
                                    estudianteId: asig.estudiante,
                                    estudianteNombre: asig.estudiante_nombre,
                                    mentorActualId: asig.mentor,
                                    mentorActualNombre: asig.mentor_nombre,
                                  })
                                }
                                title="Cambiar de mentor"
                              >
                                Cambiar mentor
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-warning-600 rounded-pill px-3"
                                onClick={() =>
                                  handleStatusChange(asig.id, "INACTIVA")
                                }
                                title="Inactivar"
                              >
                                Inactivar
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-sm btn-success-600 rounded-pill px-3"
                              onClick={() =>
                                handleStatusChange(asig.id, "ACTIVA")
                              }
                              title="Activar"
                            >
                              Activar
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn-sm btn-danger-600 rounded-pill px-3"
                            onClick={() => handleDelete(asig.id)}
                            title="Eliminar"
                          >
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-5 text-secondary-light">
              <Icon icon="mdi:account-off-outline" width="48" className="mb-2" />
              <p>Este mentor no tiene estudiantes asignados.</p>
            </div>
          )
        ) : (
          <div className="text-center py-5 text-secondary-light">
            <Icon icon="mdi:account-search" width="48" className="mb-2" />
            <p>Seleccione un mentor para ver sus asignaciones.</p>
          </div>
        )}
      </div>

      {selectedMentor && isModalOpen && (
        <AsignacionModal
          mentorId={selectedMentor.value}
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => {
            setIsModalOpen(false);
            fetchAsignaciones(selectedMentor.value);
          }}
        />
      )}

      {reasignarTarget && (
        <ReasignarMentorModal
          estudianteId={reasignarTarget.estudianteId}
          estudianteNombre={reasignarTarget.estudianteNombre}
          mentorActualId={reasignarTarget.mentorActualId}
          mentorActualNombre={reasignarTarget.mentorActualNombre}
          onClose={() => setReasignarTarget(null)}
          onSuccess={() => {
            setReasignarTarget(null);
            if (selectedMentor) fetchAsignaciones(selectedMentor.value);
          }}
        />
      )}
    </div>
  );
};

export default SeguimientoPage;
